import { useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { LANDING } from '@/constants/testIds';
import { useReveal } from '@/hooks/useReveal';

// Curated client list with sector label
const CLIENT_CARDS = [
    { name: 'GODET GROUPE', sector: 'BTP · Construction', featured: true },
    { name: 'SNCF', sector: 'Transport ferroviaire', featured: true },
    { name: 'GEODIS', sector: 'Logistique IDF', featured: true },
    { name: 'PAPREC', sector: 'Recyclage industriel', featured: true },
    { name: 'PRÉFECTURE DE PARIS', sector: 'Service public', featured: true },
    { name: 'ADP — ROISSY', sector: 'Aéroportuaire', featured: true },
    { name: 'BUT', sector: 'Retail · Distribution' },
    { name: 'GCC', sector: 'Construction' },
    { name: 'MIDAS', sector: 'Automobile · Garage' },
    { name: 'EURO REPAR', sector: 'Réparation auto' },
    { name: 'H MARKET', sector: 'Distribution alimentaire' },
    { name: 'GREEN RECUP', sector: 'Recyclage écologique' },
    { name: 'CNGT', sector: 'Industrie' },
    { name: 'AIR CRAFT — CABINET D\'AIRE', sector: 'Aéronautique' },
    { name: 'FONBONNE', sector: 'BTP' },
    { name: 'OKARRE', sector: 'Industrie' },
    { name: 'KINTESSIA', sector: 'Industrie' },
    { name: 'ESPACE MA BOUCHERIE', sector: 'Boucherie · Cuisine' },
    { name: 'DISTRI PNEU', sector: 'Automobile' },
    { name: 'MEDERREG', sector: 'Médical' },
    { name: 'FLY CAR', sector: 'Aéroportuaire' },
    { name: 'FOURGO', sector: 'Transport' },
    { name: 'MONTAGRU', sector: 'Construction' },
    { name: 'PRÉFECTURE ROISSY — ADP', sector: 'Aéroportuaire' },
];

const INITIAL = 8;

export const Clients = () => {
    const [ref, visible] = useReveal();
    const [expanded, setExpanded] = useState(false);
    const visibleCards = expanded ? CLIENT_CARDS : CLIENT_CARDS.slice(0, INITIAL);

    return (
        <section
            id="clients"
            data-testid={LANDING.clients}
            ref={ref}
            className="relative py-20 sm:py-28 border-t border-white/5"
        >
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
                <div className={`${visible ? 'in-view' : ''} reveal flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10 sm:mb-14`}>
                    <div>
                        <span className="section-label">06 / Ils nous font confiance</span>
                        <h2 className="mt-4 font-display uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.9] text-white">
                            Nous habillons
                            <br />
                            <span className="text-outline-yellow">un grand nombre</span>
                            <br />
                            de sociétés
                        </h2>
                    </div>
                    <p className="max-w-md text-base sm:text-lg text-white leading-relaxed">
                        Groupes industriels, services publics, ateliers, PME : la confiance s'écrit dans la durée. Des <strong>milliers de clients</strong> équipés par MANU VET — voici quelques-uns d'entre eux.
                    </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {visibleCards.map((c, i) => (
                        <div
                            key={c.name}
                            className={`group relative corner-cut bg-[#0F0F0F] border ${c.featured ? 'border-[#FFD500]/40' : 'border-white/15'} hover:border-[#FFD500]/80 transition-all duration-500 p-5 sm:p-6 min-h-[140px] flex flex-col justify-between ${
                                visible ? 'in-view reveal' : 'reveal'
                            }`}
                            style={{ animationDelay: `${0.04 + (i % INITIAL) * 0.05}s` }}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-xs uppercase tracking-[0.22em] text-white/55 font-mono">
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                {c.featured && (
                                    <span className="text-[9px] uppercase tracking-[0.22em] text-[#FFD500] font-bold">
                                        Réf.
                                    </span>
                                )}
                            </div>
                            <div>
                                <div className="font-display uppercase text-base sm:text-lg text-white leading-tight">
                                    {c.name}
                                </div>
                                <div className="mt-1.5 text-xs uppercase tracking-[0.18em] text-white/75">
                                    {c.sector}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {CLIENT_CARDS.length > INITIAL && (
                    <div className="mt-10 flex justify-center">
                        <button
                            type="button"
                            data-testid="landing-clients-show-more"
                            onClick={() => setExpanded((v) => !v)}
                            className="group inline-flex items-center gap-3 px-6 py-3.5 bg-transparent border border-[#FFD500] text-[#FFD500] font-bold uppercase tracking-wider text-sm hover:bg-[#FFD500] hover:text-black transition-colors corner-cut"
                        >
                            <ChevronDown
                                className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                            />
                            <span>{expanded ? 'Voir moins' : 'En savoir plus'}</span>
                            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};
